import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import moment from 'moment';
import { ScrollSync } from 'react-virtualized';
import styles from './AsnUpdatesTable.module.css';
import {
  AGGREGATES,
  AGGREGATE_STATUS,
  ASN_STATUS,
  APPLICATION_VARIANTS,
} from '../../constants';
import {
  getFormattedAsn,
  getSelectedOptionalColumns,
  filterByNestedVcDescriptionCurry,
  compareByDateAndSerialNumber,
} from '../../utils/tableUtils';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { optionalColumns } from '../../staticData';
import VirtualizedTable from '../VirtualizedTable/VirtualizedTable';
import StatusCircle from '../StatusCircle/StatusCircle';
import HeaderSearchBar from '../HeaderSearchBar.js/HeaderSearchBar';
import config from '../../config/config';

const { APPLICATION_VARIANT } = config;
const COLORS = {
  RED: 'red',
  GREEN: 'green',
  YELLOW: 'yellow',
  BLUE: 'blue',
  WHITE: 'white',
};

const HeaderWithCounter = ({
  headerText,
  count,
  countBackgroundColor = '#48b275',
  itemsAlignment = 'center',
}) => (
  <div
    className={styles.headerWithCounterContainer}
    style={{ alignItems: itemsAlignment }}
  >
    <div
      className={styles.readyPartsCount}
      style={{ backgroundColor: countBackgroundColor }}
    >
      {count}
    </div>
    <div className={styles.headerText}>{headerText}</div>
  </div>
);

const AsnUpdatesTable = ({ rows, selectedOptionalColNames, agsn }) => {
  const ROW_HEIGHT = 38;
  const HEADER_ROW_HEIGHT = 50;
  const PADDING_ROWS_COUNT = 10;
  const [rowsData, setRowsData] = useState([...rows]);
  const [descriptionSearchText, setDescriptionSearchText] = useState('');
  const [vcAliasSearchText, setVcAliasSearchText] = useState('');
  const [vcNumberSearchText, setVcNumberSearchText] = useState('');
  const [line, setLine] = usePlantAndLinesStore().lineState;
  const [plant] = usePlantAndLinesStore().plantState;
  const getDynamicRowHeight = index => {
    const row = rowsData[index - 1];
    if (!row) return ROW_HEIGHT;
    const {
      frameStatuses,
      engineStatuses,
      rearAxleStatuses,
      frontAxleStatuses,
      cabStatuses,
      scmMaterialStatuses,
      paintStatuses,
    } = row;
    const maxChildMaterials = Math.max(
      frameStatuses?.length ?? 0,
      engineStatuses?.length ?? 0,
      rearAxleStatuses?.length ?? 0,
      frontAxleStatuses?.length ?? 0,
      cabStatuses?.length ?? 0,
      scmMaterialStatuses?.length ?? 0,
      paintStatuses?.length ?? 0,
    );

    return ROW_HEIGHT * Math.ceil(maxChildMaterials / 2) || ROW_HEIGHT;
  };
  const getRowHeight = ({ index }) =>
    index === 0 ? HEADER_ROW_HEIGHT : getDynamicRowHeight(index);
  const tableRowColors = ['#1e1e1e', '#272727'];

  const chassisStatusColorIdentifier = status => {
    if (status === ASN_STATUS.LINED_OUT) return COLORS.RED;
    if (status === ASN_STATUS.DROPPED) return COLORS.YELLOW;
    if (status === ASN_STATUS.READY_FOR_DROP) return COLORS.WHITE;
    if (
      status === ASN_STATUS.ROLLED_OUT ||
      status === ASN_STATUS.WTCL ||
      status === ASN_STATUS.SFLT
    )
      return COLORS.GREEN;
    return null;
  };

  const colorByAggregateStatus = new Map([
    [AGGREGATE_STATUS.BOOKED, COLORS.GREEN],
    [AGGREGATE_STATUS.READY_FOR_DROP, COLORS.WHITE],
    [AGGREGATE_STATUS.DROPPED, COLORS.YELLOW],
    [AGGREGATE_STATUS.READY_FOR_PICKUP, COLORS.GREEN],
    [AGGREGATE_STATUS.LINE_OUT, COLORS.RED],
    [AGGREGATE_STATUS.DISPATCHED, COLORS.BLUE],
  ]);
  const aggregateStatusColorIdentifier = status =>
    colorByAggregateStatus.get(status);

  // replace the emergencyAsn to emergencyFlag field in API then same will replicate in UI as well
  const identifyTextColorClassOfRowForDropTime = ({
    emergencyFlag,
    lineOnFlag,
    status,
  }) => {
    if (emergencyFlag || lineOnFlag) return styles.yellowText;
    if (status === ASN_STATUS.LINED_OUT) return styles.redText;
    return styles.greenText;
  };

  const identifyTextColorClassOfRowForAsn = row => {
    const isLinedOutAsn = row.status === ASN_STATUS.LINED_OUT;
    const isAsnOutOfSequence = row.emergencyFlag || row.lineOnFlag;
    if (isLinedOutAsn) {
      return styles.redText;
    } else if (isAsnOutOfSequence) {
      return styles.yellowText;
    }
    return styles.greenText;
  };

  const checkIfPartIsReadyForPickUpOrBooked = (materialGroups, partName) => {
    return (
      materialGroups.filter(
        group =>
          group.name === partName &&
          (group.materials[0].aggregateStatus ===
            AGGREGATE_STATUS.READY_FOR_PICKUP ||
            group.materials[0].aggregateStatus === AGGREGATE_STATUS.BOOKED),
      ).length > 0
    );
  };

  const getReadyAgregatePartCount = (asnRows, partName) => {
    return asnRows.filter(asn =>
      checkIfPartIsReadyForPickUpOrBooked(asn.materialGroupClusters, partName),
    ).length;
  };

  const fixedColumns = [
    {
      width: 55,
      title: '#',
      field: 'sequence',
      render: (_, idx) => idx,
    },
    {
      width: 120,
      title: 'ASN',
      field: 'asn',
      headerStyles: { paddingTop: 3, paddingBottom: 0 },
      render: row => (
        <span className={identifyTextColorClassOfRowForAsn(row)}>
          {getFormattedAsn(row)}
        </span>
      ),
      renderHeader: () => (
        <HeaderWithCounter
          headerText={agsn?.length > 0 ? 'AGSN' : 'ASN'}
          count={rowsData.length}
          countBackgroundColor="#a76100"
          itemsAlignment="baseline"
        />
      ),
    },
    {
      width: 300,
      title: 'Description',
      field: agsn?.length > 0 ? 'materialDescription' : 'vcDescription',
      cellStyles: {
        fontWeight: 'bold',
      },
      renderHeader: () => (
        <>
          <HeaderSearchBar
            title="Description"
            handleChange={e => setDescriptionSearchText(e.target.value)}
            clearData={() => setDescriptionSearchText('')}
            placeholder="Search VC Description"
            value={descriptionSearchText}
            iconProps={{ className: styles.searchIcon }}
          />
        </>
      ),
    },
    {
      width: 70,
      title: 'Shift',
      field: 'shift',
      headerStyles: { textAlign: 'center' },
      render: row => {
        const currentChassisStatus = row.vcStatus || row.status;
        const currentShiftLabel = row.shiftName;

        if (currentShiftLabel)
          return (
            <div
              style={{
                width: '22px',
                height: '22px',
                lineHeight: '22px',
                borderRadius: '5px',
                backgroundColor: deriveShiftBackgroundColor(
                  currentChassisStatus,
                  currentShiftLabel,
                ),
                color: 'white',
                textAlign: 'center',
                marginTop: '-4px',
                marginLeft: '1em',
              }}
            >
              {currentShiftLabel}
            </div>
          );

        return null;
      },
      
    },
     ...(agsn?.length > 0 ? [{
    width: 280,
    title: 'Vc Alias',
    field: 'vcAlias',
    cellStyles: {
      fontWeight: 'bold',
    },
    renderHeader: () => (
      <>
        <HeaderSearchBar
          title="Vc Alias"
          handleChange={e => setVcAliasSearchText(e.target.value)}
          clearData={() => setVcAliasSearchText('')}
          placeholder="Search VC Alias"
          value={vcAliasSearchText}
          iconProps={{ className: styles.searchIcon }}
        />
      </>
    ),
  }] : [{
      width: 240,
      title: 'Vc',
      field: 'vc',
      cellStyles: {
        fontWeight: 'bold',
      },
      renderHeader: () => (
        <>
          <HeaderSearchBar
            title="VC"
            handleChange={e => setVcNumberSearchText(e.target.value)}
            clearData={() => setVcNumberSearchText('')}
            placeholder="Search VC Number"
            value={vcNumberSearchText}
            iconProps={{ className: styles.searchIcon }}
          />
        </>
      ),
    },]),
  ];

useEffect(() => {
  const filteredData = rows.filter(row => {
    const descriptionMatch = descriptionSearchText 
      ? (agsn?.length > 0 
          ? row.materialDescription?.toLowerCase().includes(descriptionSearchText.toLowerCase())
          : filterByNestedVcDescriptionCurry(descriptionSearchText)(row))
      : true;
    const vcNumberMatch = vcNumberSearchText && !agsn?.length
      ? row.vc?.toLowerCase().includes(vcNumberSearchText.toLowerCase())
      : true;
    // Filter by VC Alias if search text exists (only for AGSN)
    const vcAliasMatch = vcAliasSearchText && agsn?.length > 0
      ? row.vcAlias?.toLowerCase().includes(vcAliasSearchText.toLowerCase())
      : true;
    
    return descriptionMatch && vcNumberMatch && vcAliasMatch;
  });

  const sortedDataByDateAndSerialNumber = filteredData.sort((a, b) =>
    compareByDateAndSerialNumber(a, b, 'date', 'sequence'),
  );
  setRowsData(sortedDataByDateAndSerialNumber);
}, [descriptionSearchText, vcNumberSearchText, vcAliasSearchText, rows, agsn]);

  const deriveShiftBackgroundColor = (chassisStatus, shiftLabel) => {
    let backgroundColor = '#A7A7A7';
    if (isShiftModifiable(chassisStatus)) {
      return backgroundColor;
    }
    const shiftBackgroundColorsMap = {
      A: '#52A281',
      B: '#0275A7',
      C: '#0066CC',
      G: '#608FC9',
    };
    backgroundColor = shiftBackgroundColorsMap[shiftLabel];
    return backgroundColor;
  };

  const isShiftModifiable = chassisStatus => {
    return !(
      chassisStatus === ASN_STATUS.ASN_GENERATED ||
      chassisStatus === ASN_STATUS.LINED_OUT
    );
  };

  const fixedColumnsStyled = fixedColumns.map(column => ({
    ...column,
    headerStyles: {
      color: '#A76100',
      fontSize: '1em',
      fontWeight: '600',
      boxSizing: 'border-box',
      ...column.headerStyles,
    },
  }));

  const optionalColumnsStyles = {
    headerStyles: {
      color: '#fff',
      zIndex: 1,
      boxSizing: 'border-box',
    },
  };

  const selectedOptionalColumns = getSelectedOptionalColumns(
    optionalColumns,
    selectedOptionalColNames,
    optionalColumnsStyles,
  );

  const MATERIAL_COL_WIDTH = 80;

  const dataColumns = [
    {
      width: 100,
      title: 'Drop Time',
      field: '_dropTime',
      style: { textAlign: 'center' },
      cellStyles: {
        background: '#000',
      },
      headerStyles: {
        color: '#D8D8D8',
      },
      render: row =>
        row.droppedAt ? (
          <span className={identifyTextColorClassOfRowForDropTime(row)}>
            {moment(row.droppedAt).format('hh:mm a')}
          </span>
        ) : (
          '-'
        ),
    },
    {
      width: 100,
      title:
        APPLICATION_VARIANT === APPLICATION_VARIANTS.MES4_EV || plant?.label?.toLowerCase() === "pantnagar"
          ? 'TCF'
          : 'Chassis',
      field: 'status',
      cellStyles: {
        background: '#000',
      },
      style: { textAlign: 'center', whiteSpace: 'normal' },
      render: ({ status, reason }) => (
        <StatusCircle
          color={chassisStatusColorIdentifier(status)}
          hoverText={reason}
        />
      ),
    },
   {
      width: MATERIAL_COL_WIDTH,
      title: 'Frame',
      field: 'frameStatuses',
      style: { textAlign: 'center', whiteSpace: 'normal' },
      headerStyles: { paddingTop: 3, paddingBottom: 0 },

      render: ({ frameStatuses = [AGGREGATE_STATUS.PLANNED], asn }) =>
        frameStatuses.map(
          (
            {
              status,
              material,
              aggregatePlanningReason,
              aggregateSerialNumber,
              aggregateAgsnId,
              aggregateAgsn,
            },
            index,
          ) => (
            <StatusCircle
              color={aggregateStatusColorIdentifier(status)}
              hoverText={material ? `Part - ${material}` : ' '}
              hoverAsSNText={
                aggregateAgsnId ? `AgSN-Id - ${aggregateAgsnId}` : ' '
              }
              hoverSerialText={
                aggregateSerialNumber ? `Sr No - ${aggregateSerialNumber}` : ' '
              }
              hoverReasonText={
                aggregatePlanningReason
                  ? `Reason - ${aggregatePlanningReason}`
                  : ' '
              }
              hoverAgsnText={
                aggregateAgsn ? `AgSN - ${aggregateAgsn}` : ' '
              }
              data-testid={`frameStatus-${asn}-${index}`}
            />
          ),
        ),
      renderHeader: () => (
        <HeaderWithCounter
          headerText="Frame"
          count={getReadyAgregatePartCount(rowsData, AGGREGATES.FRAME)}
        />
      ),
    },
    {
      width: MATERIAL_COL_WIDTH,
      title: 'Engine',
      field: 'engineStatuses',
      style: { textAlign: 'center', whiteSpace: 'normal' },
      headerStyles: { paddingTop: 3, paddingBottom: 0 },
      render: ({ engineStatuses = [AGGREGATE_STATUS.PLANNED], asn }) =>
        engineStatuses.map(
          (
            {
              status,
              material,
              aggregatePlanningReason,
              aggregateSerialNumber,
              aggregateAgsnId,
              aggregateAgsn,
            },
            index,
          ) => (
            <StatusCircle
              color={aggregateStatusColorIdentifier(status)}
              hoverText={material ? `Part - ${material}` : ' '}
              hoverAsSNText={
                aggregateAgsnId ? `AgSN-Id - ${aggregateAgsnId}` : ' '
              }
              hoverSerialText={
                aggregateSerialNumber ? `Sr No - ${aggregateSerialNumber}` : ' '
              }
              hoverReasonText={
                aggregatePlanningReason
                  ? `Reason - ${aggregatePlanningReason}`
                  : ' '
              }
              hoverAgsnText={
                aggregateAgsn ? `AgSN - ${aggregateAgsn}` : ' '
              }
              data-testid={`engineStatus-${index}`}
            />
          ),
        ),
      renderHeader: () => (
        <HeaderWithCounter
          headerText="Engine"
          count={getReadyAgregatePartCount(rowsData, AGGREGATES.ENGINE)}
        />
      ),
    },
    {
      width: MATERIAL_COL_WIDTH,
      title: 'R Axle',
      field: 'rearAxleStatuses',
      style: { textAlign: 'center', whiteSpace: 'normal' },
      headerStyles: { paddingTop: 3, paddingBottom: 0 },
      render: ({ rearAxleStatuses = [AGGREGATE_STATUS.PLANNED], asn }) =>
        rearAxleStatuses.map(
          (
            {
              status,
              material,
              aggregatePlanningReason,
              aggregateSerialNumber,
              aggregateAgsnId,
              aggregateAgsn,
            },
            index,
          ) => (
            <StatusCircle
              color={aggregateStatusColorIdentifier(status)}
              hoverText={material ? `Part - ${material}` : ' '}
              hoverAsSNText={
                aggregateAgsnId ? `AgSN-Id - ${aggregateAgsnId}` : ' '
              }
              hoverSerialText={
                aggregateSerialNumber ? `Sr No - ${aggregateSerialNumber}` : ' '
              }
              hoverReasonText={
                aggregatePlanningReason
                  ? `Reason - ${aggregatePlanningReason}`
                  : ' '
              }
              hoverAgsnText={
                aggregateAgsn ? `AgSN - ${aggregateAgsn}` : ' '
              }
              data-testid={`rearAxleStatus-${asn}-${index}`}
            />
          ),
        ),
      renderHeader: () => (
        <HeaderWithCounter
          headerText="R Axle"
          count={getReadyAgregatePartCount(rowsData, AGGREGATES.REAR_AXLE)}
        />
      ),
    },
    {
      width: MATERIAL_COL_WIDTH,
      title: 'F Axle',
      field: 'frontAxleStatuses',
      style: { textAlign: 'center', whiteSpace: 'normal' },
      headerStyles: { paddingTop: 3, paddingBottom: 0 },
      render: ({ frontAxleStatuses = [AGGREGATE_STATUS.PLANNED], asn }) =>
        frontAxleStatuses.map(
          (
            {
              status,
              material,
              aggregatePlanningReason,
              aggregateSerialNumber,
              aggregateAgsnId,
              aggregateAgsn,
            },
            index,
          ) => (
            <StatusCircle
              color={aggregateStatusColorIdentifier(status)}
              hoverText={material ? `Part - ${material}` : ' '}
              hoverAsSNText={
                aggregateAgsnId ? `AgSN-Id - ${aggregateAgsnId}` : ' '
              }
              hoverSerialText={
                aggregateSerialNumber ? `Sr No - ${aggregateSerialNumber}` : ' '
              }
              hoverReasonText={
                aggregatePlanningReason
                  ? `Reason - ${aggregatePlanningReason}`
                  : ' '
              }
              hoverAgsnText={
                aggregateAgsn ? `AgSN - ${aggregateAgsn}` : ' '
              }
              data-testid={`frontAxelStatus-${asn}-${index}`}
            />
          ),
        ),
      renderHeader: () => (
        <HeaderWithCounter
          headerText="F Axle"
          count={getReadyAgregatePartCount(rowsData, AGGREGATES.FRONT_AXLE)}
        />
      ),
    },
    ((plant?.label?.toLowerCase() === "pantnagar" )|| line?.label === 'E9')
    ? {
        width: MATERIAL_COL_WIDTH,
        title: 'Paint',
        field: 'paintStatuses',
        style: { textAlign: 'center', whiteSpace: 'normal' },
        headerStyles: { paddingTop: 3, paddingBottom: 0 },
        render: ({ paintStatuses = [AGGREGATE_STATUS.PLANNED], asn }) =>
          paintStatuses.map(
            (
              {
                status,
                material,
                aggregatePlanningReason,
                aggregateSerialNumber,
                aggregateAgsnId,
                aggregateAgsn,
              },
              index,
            ) => (
              <StatusCircle
                color={aggregateStatusColorIdentifier(status)}
                hoverText={material ? `Part - ${material}` : ' '}
                hoverAsSNText={
                  aggregateAgsnId ? `AgSN-Id - ${aggregateAgsnId}` : ' '
                }
                hoverSerialText={
                  aggregateSerialNumber
                    ? `Sr No - ${aggregateSerialNumber}`
                    : ' '
                }
                hoverReasonText={
                  aggregatePlanningReason
                    ? `Reason - ${aggregatePlanningReason}`
                    : ' '
                }
                hoverAgsnText={
                  aggregateAgsn ? `AgSN - ${aggregateAgsn}` : ' '
                }
                data-testid={`paintStatus-${asn}-${index}`}
              />
            ),
          ),
        renderHeader: () => (
          <HeaderWithCounter
            headerText="Paint"
            count={getReadyAgregatePartCount(rowsData, AGGREGATES.PAINT)}
          />
        ),
      }
    : {
        width: MATERIAL_COL_WIDTH,
        title: 'Cab',
        field: 'cabStatuses',
        style: { textAlign: 'center', whiteSpace: 'normal' },
        headerStyles: { paddingTop: 3, paddingBottom: 0 },
        render: ({ cabStatuses = [AGGREGATE_STATUS.PLANNED], asn }) =>
          cabStatuses.map(
            (
              {
                status,
                material,
                aggregatePlanningReason,
                aggregateSerialNumber,
                aggregateAgsnId,
                aggregateAgsn,
              },
              index,
            ) => (
              <StatusCircle
                color={aggregateStatusColorIdentifier(status)}
                hoverText={material ? `Part - ${material}` : ' '}
                hoverAsSNText={
                  aggregateAgsnId ? `AgSN-Id - ${aggregateAgsnId}` : ' '
                }
                hoverSerialText={
                  aggregateSerialNumber
                    ? `Sr No - ${aggregateSerialNumber}`
                    : ' '
                }
                hoverReasonText={
                  aggregatePlanningReason
                    ? `Reason - ${aggregatePlanningReason}`
                    : ' '
                }
                hoverAgsnText={
                  aggregateAgsn ? `AgSN - ${aggregateAgsn}` : ' '
                }
                data-testid={`cabStatus-${asn}-${index}`}
              />
            ),
          ),
        renderHeader: () => (
          <HeaderWithCounter
            headerText="Cab"
            count={getReadyAgregatePartCount(rowsData, AGGREGATES.CAB)}
          />
        ),
      },
    {
      width: MATERIAL_COL_WIDTH,
      title: 'Material',
      field: 'scmMaterialStatuses',
      style: { textAlign: 'center', whiteSpace: 'normal' },
      headerStyles: { paddingTop: 3, paddingBottom: 0 },
      render: ({ scmMaterialStatuses = [AGGREGATE_STATUS.PLANNED], asn }) =>
        scmMaterialStatuses.map(
          (
            {
              status,
              material,
              aggregatePlanningReason,
              aggregateSerialNumber,
            },
            index,
          ) => (
            <StatusCircle
              color={aggregateStatusColorIdentifier(status)}
              hoverText={material ? `Part - ${material}` : ' '}
              hoverReasonText={aggregatePlanningReason}
              data-testid={`scmMaterialStatus-${asn}-${index}`}
            />
          ),
        ),
      renderHeader: () => (
        <HeaderWithCounter
          headerText="Material"
          count={getReadyAgregatePartCount(rowsData, AGGREGATES.MATERIAL)}
        />
      ),
    },
  ];
  const dataColumnsForAgsn = [
    {
      width: 100,
      title: 'Drop Time',
      field: '_dropTime',
      style: { textAlign: 'center' },
      cellStyles: {
        background: '#000',
      },
      headerStyles: {
        color: '#D8D8D8',
      },
      render: row =>
        row.droppedAt ? (
          <span className={identifyTextColorClassOfRowForDropTime(row)}>
            {moment(row.droppedAt).format('hh:mm a')}
          </span>
        ) : (
          '-'
        ),
    },
    {
      width: 100,
      title: agsn,
      field: 'status',
      cellStyles: {
        background: '#000',
      },
      style: { textAlign: 'center', whiteSpace: 'normal' },
      render: ({ status, reason }) => (
        <StatusCircle
          color={chassisStatusColorIdentifier(status)}
          hoverText={reason}
        />
      ),
    },
      ...(agsn === AGGREGATES.ENGINE  ? [{
      width: MATERIAL_COL_WIDTH,
      title: 'Gear Box',
      field: 'gearboxStatuses',
      style: { textAlign: 'center', whiteSpace: 'normal' },
      headerStyles: { paddingTop: 3, paddingBottom: 0 },

      render: ({ gearboxStatuses = [AGGREGATE_STATUS.PLANNED], asn }) =>
        gearboxStatuses.map(
          (
            {
              status,
              material,
              aggregatePlanningReason,
              aggregateSerialNumber,
              aggregateAgsnId,
            },
            index,
          ) => (
            <StatusCircle
              color={aggregateStatusColorIdentifier(status)}
              hoverText={material ? `Part - ${material}` : ' '}
              hoverAsSNText={
                aggregateAgsnId ? `AgSN - ${aggregateAgsnId}` : ' '
              }
              hoverSerialText={
                aggregateSerialNumber ? `Sr No - ${aggregateSerialNumber}` : ' '
              }
              hoverReasonText={
                aggregatePlanningReason
                  ? `Reason - ${aggregatePlanningReason}`
                  : ' '
              }
              data-testid={`gearboxStatus-${index}`}
            />
          ),
        ),
      renderHeader: () => (
            <HeaderWithCounter
              headerText="Gear Box"
              count={getReadyAgregatePartCount(rowsData, AGGREGATES.GEAR_BOX)}
            />
      )
    },] : []),
 ...(agsn === AGGREGATES.CAB ? [{
      width: MATERIAL_COL_WIDTH,
      title: 'Paint',
      field: 'paintStatuses',
      style: { textAlign: 'center', whiteSpace: 'normal' },
      headerStyles: { paddingTop: 3, paddingBottom: 0 },

      render: ({ paintStatuses = [AGGREGATE_STATUS.PLANNED], asn }) =>
        paintStatuses.map(
          (
            {
              status,
              material,
              aggregatePlanningReason,
              aggregateSerialNumber,
              aggregateAgsnId,
            },
            index,
          ) => (
            <StatusCircle
              color={aggregateStatusColorIdentifier(status)}
              hoverText={material ? `Part - ${material}` : ' '}
              hoverAsSNText={
                aggregateAgsnId ? `AgSN - ${aggregateAgsnId}` : ' '
              }
              hoverSerialText={
                aggregateSerialNumber ? `Sr No - ${aggregateSerialNumber}` : ' '
              }
              hoverReasonText={
                aggregatePlanningReason
                  ? `Reason - ${aggregatePlanningReason}`
                  : ' '
              }
              data-testid={`paintStatus-${index}`}
            />
          ),
        ),
      renderHeader: () => (
           <HeaderWithCounter
              headerText="Paint"
              count={getReadyAgregatePartCount(rowsData, AGGREGATES.PAINT)}
            />
      )
    },] : []),
    {
      width: MATERIAL_COL_WIDTH,
      title: 'Material',
      field: 'scmMaterialStatuses',
      style: { textAlign: 'center', whiteSpace: 'normal' },
      headerStyles: { paddingTop: 3, paddingBottom: 0 },
      render: ({ scmMaterialStatuses = [AGGREGATE_STATUS.PLANNED], asn }) =>
        scmMaterialStatuses.map(
          (
            {
              status,
              material,
              aggregatePlanningReason,
              aggregateSerialNumber,
            },
            index,
          ) => (
            <StatusCircle
              color={aggregateStatusColorIdentifier(status)}
              hoverText={material ? `Part - ${material}` : ' '}
              hoverReasonText={aggregatePlanningReason}
              data-testid={`scmMaterialStatus-${asn}-${index}`}
            />
          ),
        ),
      renderHeader: () => (
        <HeaderWithCounter
          headerText="Material"
          count={getReadyAgregatePartCount(rowsData, AGGREGATES.MATERIAL)}
        />
      ),
    },
  ];

  const baseColumns = () => {
  let cols = dataColumns;
  // Hide Frame, cab only for Pantnagar plant
  if (plant?.label?.toLowerCase() === "pantnagar") {
    cols = cols.filter(
      col =>
        col.title !== 'Frame' &&
        col.title !== 'Cab'
    );
  }
  if (line.label === 'E9') {
    cols = cols.filter(col => col.title !== 'Frame');
  }
  if (agsn?.length > 0) return dataColumnsForAgsn;
  return cols;
};
 
  return (
    <>
      <div style={{ height: '95%' }} className={styles.container}>
        <div style={{ height: '100%' }} className={styles.tableView}>
          <ScrollSync>
            {({ onScroll, scrollTop }) => (
              <div
                style={{ height: '100%' }}
                className={clsx(styles.sequenceScroll)}
              >
                <VirtualizedTable
                  tableWidth={'44%'}
                  columns={[...fixedColumnsStyled, ...selectedOptionalColumns]}
                  rows={rowsData}
                  enableAutoHeight
                  rowHeight={getRowHeight}
                  onScroll={onScroll}
                  scrollTop={scrollTop}
                  classNames={{
                    tableContainerClassName:
                      styles.attributesTableChildMaterial,
                    tableHeadClassName: styles.tableHead,
                    tableCellClassName: styles.mTablecell,
                  }}
                  rowColors={tableRowColors}
                  fixedColumnCount={2}
                  classNameBottomLeftGrid={clsx(
                    styles.BottomLft,
                    styles.hideVerticalScroll,
                  )}
                  classNameBottomRightGrid={clsx(
                    styles.BottomRgt,
                    styles.hideVerticalScroll,
                    styles.scroll,
                  )}
                  classNameTopLeftGrid={styles.TopLft}
                  classNameTopRightGrid={styles.TopRgt}
                  maxEmptyRowPadding={PADDING_ROWS_COUNT}
                />
                <VirtualizedTable
                  tableWidth={'55%'}
                  columns={[...baseColumns()]}
                  enableAutoHeight
                  rows={rowsData}
                  rowHeight={getRowHeight}
                  onScroll={onScroll}
                  scrollTop={scrollTop}
                  classNames={{
                    tableContainerClassName: clsx(
                      styles.attributesTable,
                      styles.RgtTable,
                    ),
                    tableHeadClassName: styles.tableHead,
                    tableCellClassName: styles.mTablecell,
                  }}
                  rowColors={tableRowColors}
                  classNameBottomLeftGrid={clsx(
                    styles.BottomLast,
                    styles.scroll,
                  )}
                  classNameBottomRightGrid={clsx(
                    styles.BottomRgt,
                    styles.scroll,
                  )}
                  classNameTopLeftGrid={styles.TopLft}
                  classNameTopRightGrid={styles.TopRgt}
                  maxEmptyRowPadding={PADDING_ROWS_COUNT}
                />
              </div>
            )}
          </ScrollSync>
        </div>
      </div>
    </>
  );
};
AsnUpdatesTable.propTypes = {
  rows: PropTypes.array,
  selectedOptionalColNames: PropTypes.array,
  agsn: PropTypes.string,
};

export default AsnUpdatesTable;
